import { BoxComponent, ButtonComponent, DialogComponent, TypographyComponent } from '@zeus/web-components';
import Image from 'next/image';
import { styles } from './CartStatusNotifier.styles';
import { Dispatch, SetStateAction } from 'react';
import { getImage } from './../../../../../../utils/UtilFunctions';
import { AmountType } from '@zeus/shared';
import { CartItem, CartReducerActionType } from '../../../types';
import { useMediaQuery, useTheme } from '@mui/material';
import { useCartActions } from '@store/online/cart/cartHooks';
import { useRouter } from 'next/navigation';
interface CartStatusNotifierProps {
  setIsDealRemoved: Dispatch<SetStateAction<boolean>>;
  dealsToBeRemoved: any;
  actionType?: CartReducerActionType;
  quantity?: number;
  setQuantity?: Dispatch<SetStateAction<number>>;
  item?: CartItem;
  oldItem?: CartItem;
  itemIndex?: number;
  routeBack?: boolean;
  isDealExpired?: boolean;
  isQr?: boolean | undefined;
}
const CartStatusNotifier: React.FC<CartStatusNotifierProps> = ({
  setIsDealRemoved,
  dealsToBeRemoved,
  actionType,
  quantity,
  setQuantity,
  item,
  oldItem,
  itemIndex,
  routeBack,
  isDealExpired,
  isQr,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const cartActions = useCartActions();
  const router = useRouter();

  const handDealRemove = () => {
    const dealIds = dealsToBeRemoved?.map((deal: any) => Number(deal?.dealId));
    const updatedPayload = {
      dealIds: dealIds,
      actionType,
      ...(actionType === CartReducerActionType.DecrementInCart && item && { updatedItem: item }),
      ...(actionType === CartReducerActionType.DeleteItem && item && { id: item.id, itemIndex }),
      ...(actionType === CartReducerActionType.UpdateInCart &&
        item &&
        oldItem && { updatedItem: item, oldCartItem: oldItem }),
    };

    cartActions.removeDealFromCart(updatedPayload);
    setIsDealRemoved(false);

    // Navigate back to cart if routeBack is true
    if (routeBack) {
      router.back();
    }
  };

  return (
    <DialogComponent
      id="cart-removal-dialog"
      open
      PaperProps={{
        sx: {
          width: { md: '740px' }, // Default to 90vw for small screens, 822px for medium and larger screens
          maxWidth: '100%', // To ensure it does not overflow viewport width
          height: { md: '550px' }, // Default to 90vh for small screens, fixed height for medium and larger screens
        },
      }}
    >
      <BoxComponent
        sx={{
          padding: '0 10px 20px 10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          width: { xs: '90%', sm: '70%' },
          margin: 'auto',
        }}
      >
        <BoxComponent sx={styles.ItemRemovalInfoContainer}>
          <TypographyComponent sx={styles.title} id="cart-items__removal-title">
            {isDealExpired ? 'Deal expired' : 'Remove item ?'}
          </TypographyComponent>
          <TypographyComponent sx={styles.description} id="cart-items__removal-description">
            {isDealExpired
              ? 'The deal you added has expired and is no longer available in your cart'
              : dealsToBeRemoved.length > 1
              ? 'If you remove these items, you won’t meet the minimum spend criteria and will lose these deals'
              : 'If you remove this item, you won’t meet the minimum spend criteria and will lose this deal'}
          </TypographyComponent>
        </BoxComponent>

        {dealsToBeRemoved &&
          dealsToBeRemoved.length > 0 &&
          dealsToBeRemoved.map((deal: any, index: number) => (
            <BoxComponent sx={styles.imageContainer} key={index} id={`cart-items__deal-image-container${index}`}>
              <BoxComponent sx={styles.dealImageStyling} id="cart-items__deal-image">
                <Image
                  src={getImage(deal?.deal?.images && deal?.deal?.images.length > 0 ? deal?.deal?.images[0] : '').url}
                  width={80}
                  height={80}
                  alt="cart status notifier img"
                  style={{ borderRadius: '10px', objectFit: 'cover' }}
                />
                <BoxComponent sx={styles.container}>
                  <TypographyComponent sx={styles.dealAppliedtag}>Deal Applied</TypographyComponent>
                </BoxComponent>
              </BoxComponent>

              <BoxComponent sx={{ ...styles.offPriceContainer, flexDirection: 'column' }} id="cart-items__deal-details">
                <BoxComponent sx={styles.offPriceContainer}>
                  <TypographyComponent sx={styles.discountLabel}>
                    {deal?.deal?.name ? deal?.deal?.name : ''}
                  </TypographyComponent>{' '}
                  {(deal?.deal?.details as any)?.isEntire ? (
                    <TypographyComponent
                      id="cart-items__discount-price"
                      variant="subtitle2"
                      sx={{ color: '#242528', fontSize: { md: '18px' }, fontWeight: 500 }}
                    >
                      -{(deal?.deal?.details as any)?.type === AmountType.PRICE && '$'}
                      {Number((deal?.deal?.details as any)?.amount)?.toFixed(2)}
                      {(deal?.deal?.details as any)?.type === AmountType.PERCENTAGE && '%'}
                    </TypographyComponent>
                  ) : (
                    <>
                      {deal?.discountedPrice == 0 ? (
                        <TypographyComponent variant="subtitle2" sx={styles.priceLabel}>
                          Free
                        </TypographyComponent>
                      ) : (
                        <TypographyComponent
                          id="cart-items__discount-price"
                          variant="subtitle2"
                          sx={{ color: '#242528', fontSize: '12px', fontWeight: 500 }}
                        >
                          {(deal?.discountedPrice ?? 0).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </TypographyComponent>
                      )}
                    </>
                  )}
                </BoxComponent>
                {!isDealExpired && (
                  <BoxComponent sx={{ ...styles.offPriceContainer, marginTop: '10px' }} id="cart-items__minimum-spend">
                    <TypographyComponent sx={{ ...styles.discountLabel, fontSize: '10px' }}>
                      Minimum spend required
                    </TypographyComponent>{' '}
                    <TypographyComponent
                      id="cart-items__discount-price"
                      variant="subtitle2"
                      sx={{ color: 'grey.700', fontSize: '10px', fontWeight: 400 }}
                    >
                      {(deal?.deal?.details as any)?.isMinimumSpend &&
                        (deal?.deal?.details as any)?.minimumSpend &&
                        (deal?.deal?.details as any)?.minimumSpend + '$'}
                    </TypographyComponent>
                  </BoxComponent>
                )}
              </BoxComponent>
            </BoxComponent>
          ))}
        <BoxComponent sx={styles.buttonContainer} id="cart-items__button-container">
          <ButtonComponent
            id="cart-items__remove-button"
            variant="contained"
            onClick={handDealRemove}
            style={{ width: '150px', fontWeight: 500, fontSize: '16px' }}
          >
            {isDealExpired ? 'Continue' : 'Remove'}
          </ButtonComponent>
          {isSmallScreen ? (
            <TypographyComponent
              id="cart-items__cancel-button"
              sx={styles.cancelText}
              onClick={() => {
                setIsDealRemoved(false);
              }}
            >
              Cancel
            </TypographyComponent>
          ) : (
            <ButtonComponent
              id="cart-items__cancel-button"
              variant="outlined"
              onClick={() => {
                setIsDealRemoved(false);
              }}
              style={{ width: '150px', fontWeight: 500, fontSize: '16px', cursor: 'pointer' }}
            >
              Cancel
            </ButtonComponent>
          )}
        </BoxComponent>
      </BoxComponent>
    </DialogComponent>
  );
};

export default CartStatusNotifier;
